<template>
    <b-container fluid>
        <h1 class="text-left mb-2">{{ $t('dashboard.dashboardLogs.title') }}</h1>
        <b-row>
            <b-col cols="3">
                <label for="start-datepicker">{{
                    $t('dashboard.dashboardLogs.datefrom')
                }}</label>
                <b-form-datepicker
                    id="start-datepicker"
                    v-model="start"
                    value-as-date
                    class="mb-2"
                    :locale="getLocale"
                    :min="minFrom"
                    :max="end"
                ></b-form-datepicker>
            </b-col>
            <b-col cols="3">
                <label for="start-datepicker">{{
                    $t('dashboard.dashboardLogs.dateto')
                }}</label>
                <b-form-datepicker
                    id="end-datepicker"
                    v-model="end"
                    value-as-date
                    class="mb-2"
                    :locale="getLocale"
                    :min="start"
                    :max="maxTo"
                ></b-form-datepicker>
            </b-col>
            <b-col cols="3">
                <label for="ActionSelect">{{
                    $t('dashboard.dashboardLogs.action')
                }}</label>
                <x-select
                    id="ActionSelect"
                    @changed="$_onActionSelected($event)"
                    :options="actions"
                    v-model="action"
                    :simpleMode="true"
                    :showDefaultOption="true"
                >
                </x-select>
            </b-col>
            <b-col cols="3">
                <label for="UserSelect">{{
                    $t('dashboard.dashboardLogs.user')
                }}</label>
                <x-select
                    id="UserSelect"
                    @changed="$_onUserSelected($event)"
                    :options="users"
                    v-model="user"
                    :simpleMode="true"
                    :showDefaultOption="true"
                >
                </x-select>
            </b-col>
        </b-row>
        <b-row
            ><b-col
                ><b-button variant="primary" @click="$_showData()"
                    >{{ $t('dashboard.dashboardLogs.start') }}
                    <b-spinner
                        v-if="loading"
                        small
                        class="mx-2"
                        style="align-self: center"
                        label="Loading" /></b-button></b-col
        ></b-row>
        <b-row
            ><b-col>
                <p class="mt-3" v-if="!loading && dashboardLogs.length >= 250">
                    {{ $t('dashboard.dashboardLogs.tooMuchData') }}
                </p>
                <b-table
                    sort-by.sync="timeStamp"
                    :sort-desc="true"
                    sort-icon-left
                    class="mt-3"
                    v-if="!loading"
                    striped
                    hover
                    small
                    outlined
                    bordered
                    v-show="dashboardLogs.length > 0"
                    :items="dashboardLogs"
                    :fields="dashboardLogFields"
                >
                </b-table>
                <p class="mt-3" v-if="!loading && dashboardLogs.length == 0">
                    {{ $t('dashboard.dashboardLogs.nodata') }}
                </p>
            </b-col>
        </b-row>
    </b-container>
</template>
<script>
    import { mapActions, mapGetters } from 'vuex';
    import XSelect from '../../components/BaseComponents/x-select.vue';

    export default {
        components: { 'x-select': XSelect },

        data: function () {
            return {
                start: null,
                end: null,
                minFrom: null,
                maxTo: null,
                user: null,
                action: null,
                method: null,
                activity: null,
                dashboardLogs: [],
                loading: false,
                users: [],
                actions: [],
                dashboardLogFields: [
                    {
                        key: 'timeStamp',
                        label: this.$t('dashboard.dashboardLogs.timestamp'),
                        sortable: true,
                        formatter: (value) => {
                            return new Date(value).toLocaleString(
                                this.$store.state.userInfo.locale
                            );
                        },
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'action',
                        label: this.$t('dashboard.dashboardLogs.action'),
                        sortable: true,
                        formatter: (value) => {
                            return value && this.$t(
                                `dashboard.dashboardLogs.actions.${value.toLowerCase()}`
                            );
                        },
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'method',
                        label: this.$t('dashboard.dashboardLogs.method'),
                        sortable: true,
                        formatter: (value) => {
                            return this.$t(
                                `dashboard.dashboardLogs.methods.${value.toLowerCase()}`
                            );
                        },
                        thStyle: { border: 0 }
                    },

                    {
                        key: 'user',
                        label: this.$t('dashboard.dashboardLogs.user'),
                        sortable: true,
                        thStyle: { border: 0 }
                    }
                ]
            };
        },

        mounted: function () {
            this.init();
        },

        created() {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 7);
            startDate.setHours(0, 0, 0, 0);
            this.start = startDate;

            const endDate = new Date();
            // Set time to midnight
            endDate.setHours(0, 0, 0, 0);
            this.end = endDate;
            this.maxTo = endDate;

            const minDate = new Date();
            minDate.setDate(minDate.getDate() - 365);
            this.minFrom = minDate;
        },

        computed: {
            ...mapGetters(['getLocale']),
            getOrganizationId() {
                return this.$store.state.userInfo.organizationId;
            }
        },

        methods: {
            ...mapActions(['useApiData']),

            async $_showData() {
                this.loading = true;
                const endDate = new Date(this.end);

                // As this.end is set to the chosen date on midnight (= date 00:00:00) we need to add 1 day
                // so we can select all data with a timestamp smaller than the enddate.
                endDate.setDate(this.end.getDate() + 1);
                // set time to midnight
                endDate.setHours(0, 0, 0, 0);
                const dashboardLogs = await this.$store.dispatch('useApiData', {
                    name: 'GetDashboardLogs',
                    parameters: {
                        start: this.start.toJSON(),
                        end: endDate.toJSON(),
                        user: this.user,
                        action: this.action
                    },
                    useLoader: true
                });
                this.dashboardLogs = dashboardLogs.data;
                this.loading = false;
            },

            $_onUserSelected(event) {
                this.user = event;
            },

            $_onActionSelected(event) {
                this.action = event;
            },

            async init() {
                this.loading = true;
                const adminNames = await this.$store.dispatch('useApiData', {
                    name: 'GetAdminNames'
                });
                this.users = adminNames.data.sort();
                const actions = await this.$store.dispatch('useApiData', {
                    name: 'GetDashboardLogActions'
                });

                actions.data.forEach((action) => {
                    this.actions.push({
                        value: action,
                        text: this.$t(
                            `dashboard.dashboardLogs.actions.${action.toLowerCase()}`
                        )
                    });
                });
                this.actions.sort(this.$_compareByText);
                this.$_showData();
            },

            $_compareByText(a, b) {
                return a.text.localeCompare(b.text);
            }
        }
    };
</script>

<style lang="scss">
.exportStatsModal .modal-dialog {
    position: absolute;
    margin: 0px;
    top: 15%;
    left: calc(50% - 215px);
    min-height: 40px;
    min-width: 540px;
    max-width: 540px;

    & .modal-content {
        height: 100%;
        border: none;
        border-radius: initial;
    }
}

.exportStatsModal .modal-body {
    padding: 0px;
    height: 100%;
}

@media (max-width: 767px) {
    .exportStatsModal .modal-dialog {
        top: 0 !important;
        left: 0%;
        bottom: 0 !important;
        right: 0;
        min-width: 0;
        max-width: none;
    }
}
</style>


<style lang="scss" scoped>
h2 {
    width: 100%;
}
</style>
